// One bounded operation owns its cache writes. Refresh/logout invalidate older work.
export function createCapabilityLoader(apiGet, { timeoutMs = 15000, cacheTtlMs = 30000 } = {}) {
  let cached = null;
  let expiresAt = 0;
  let active = null;
  let generation = 0;
  const cancelled = () => Object.assign(new Error('Permission request superseded'), { name: 'AbortError' });

  function clear() {
    generation += 1;
    cached = null;
    expiresAt = 0;
    active?.controller.abort(cancelled());
    active = null;
  }

  function get(forceRefresh = false) {
    if (forceRefresh) clear();
    if (cached && Date.now() < expiresAt) return Promise.resolve(cached);
    if (active) return active.promise;
    const token = ++generation;
    const controller = new AbortController();
    const operation = { controller, promise: null };
    active = operation;
    let timer;
    let onAbort;
    const aborted = new Promise((_, reject) => {
      onAbort = () => reject(controller.signal.reason || cancelled());
      controller.signal.addEventListener('abort', onAbort, { once: true });
      timer = setTimeout(() => controller.abort(Object.assign(
        new Error('Permission check timed out. Please try again.'), { code: 'PERMISSIONS_TIMEOUT' }
      )), timeoutMs);
    });
    const load = async () => {
      for (let attempt = 0; attempt < 2; attempt += 1) {
        const data = await apiGet('/admin/me', { signal: controller.signal });
        if (token !== generation || controller.signal.aborted) throw cancelled();
        if (!data || typeof data !== 'object' || !Array.isArray(data.capabilities)) {
          throw Object.assign(new Error('Permissions could not be read. Please try again.'), { code: 'INVALID_PERMISSIONS' });
        }
        const normalized = { ...data, actorId: data.actorId || data.id };
        if (normalized.isSuperAdmin && normalized.capabilities.length === 0) {
          if (attempt === 0) continue;
          throw Object.assign(new Error('Permissions are unavailable. Please try again.'), { code: 'PERMISSIONS_UNAVAILABLE' });
        }
        cached = normalized;
        expiresAt = Date.now() + cacheTtlMs;
        return normalized;
      }
    };
    operation.promise = Promise.race([load(), aborted])
      .catch(error => {
        // HTTP status wins over a generic auth label supplied by the API wrapper.
        if (error.status === 403 && error.code === 'UNAUTHENTICATED') error.code = 'UNAUTHORIZED';
        error.isUnauthenticated = error.status === 401 || error.code === 'UNAUTHENTICATED';
        error.isUnauthorized = error.status === 403 || error.code === 'UNAUTHORIZED';
        throw error;
      })
      .finally(() => {
        clearTimeout(timer);
        controller.signal.removeEventListener('abort', onAbort);
        if (active === operation) active = null;
      });
    return operation.promise;
  }
  return { get, clear };
}
